import { Button } from '@/components/ui/Button';
import { mdiClose } from '@mdi/js';
import * as React from 'react';
import { createPortal } from 'react-dom';

export interface DrawerProps {
  isOpen: boolean;
  title: string;
  width?: 'sm' | 'md' | 'lg';
  onClose: () => void;
  showHeader?: boolean;
  className?: string;
}

export const Drawer: React.FC<React.PropsWithChildren<DrawerProps>> = ({
  isOpen,
  title,
  width = 'md',
  children,
  onClose,
  showHeader = true,
  className,
}) => {
  const widthClasses = {
    sm: 'w-80', // 320px
    md: 'w-96', // 384px
    lg: 'w-[32rem]', // 512px
  };

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const drawerContent = (
    <div
      className='fixed inset-0 z-50 flex items-start justify-end bg-black bg-opacity-50 backdrop-blur-sm'
      onClick={handleBackdropClick}
    >
      <div
        className={`
          ${widthClasses[width]}
          h-full
          bg-white
          shadow-2xl
          border-l border-grey-200
          flex flex-col
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          ${className}
        `}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        {showHeader && (
          <div className='flex items-center justify-between p-6 border-b border-grey-200 bg-grey-50'>
            <h2 className='text-xl font-semibold text-grey-900'>{title}</h2>
            <Button
              icon={mdiClose}
              color='light'
              small
              onClick={onClose}
              className='hover:bg-grey-200'
              title='Close drawer'
            />
          </div>
        )}

        {/* Content */}
        <div className='flex-1 overflow-y-auto'>{children}</div>
      </div>
    </div>
  );

  // Render in portal to avoid z-index issues
  return typeof document !== 'undefined'
    ? createPortal(drawerContent, document.body)
    : null;
};
